import { CustomTable } from "components";
import { useGetBrokerSummaryList } from "../services/AnalyticsPageService";
import { useLocation } from "react-router-dom";
import { paramParsify } from "utils/queryParams";
import { useMemo, useState } from "react";
import { formatNumber } from "utils/formatNumber";
import storage from "services/storage";
import { orderBy } from "lodash";
import { Tooltip } from "@mui/material";

const brokerPerPage = 5;

export default function BrokerTable({ defaultStartDate, defaultEndDate }) {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);
  const id = storage.getCompanyID();

  const { response: brokerSummaryListResp } = useGetBrokerSummaryList({
    id,
    enableAutoFetchByParams: true,
    defaultStartDate,
    defaultEndDate,
  });

  const [total, setTotal] = useState(0);

  const headCells = [
    {
      id: "buy_code",
      label: <p className='text-xs'>BUY</p>,
    },
    {
      id: "buy_volume",
      label: <p className='text-xs'>B.Lot</p>,
    },
    {
      id: "buy_value",
      label: <p className='text-xs'>B.Val</p>,
    },
    {
      id: "buy_avg",
      label: <p className='text-xs'>B.Avg</p>,
    },
    {
      id: "idx",
      label: <p className='text-xs'>#</p>,
    },
    {
      id: "sell_code",
      label: <p className='text-xs'>SELL</p>,
    },
    {
      id: "sell_volume",
      label: <p className='text-xs'>S.Lot</p>,
    },
    {
      id: "sell_value",
      label: <p className='text-xs'>S.Val</p>,
    },
    {
      id: "sell_avg",
      label: <p className='text-xs'>S.Avg</p>,
    },
  ];

  const page = useMemo(
    () => Number(queryParams?.page ?? 1) - 1 || 0,
    [queryParams?.page],
  );

  const bodyCells = useMemo(() => {
    const brokerRawList = [...(brokerSummaryListResp?.data || [])];

    let buyArr = [];
    let sellArr = [];

    // determine which is empty buy or empty sell
    brokerRawList?.forEach((el) => {
      const isEmptyBuy = !el?.buy_volume && !el?.buy_value && !el?.buy_avg;
      const isEmptySell = !el?.sell_volume && !el?.sell_value && !el?.sell_avg;

      const modifiedEl = {
        ...el,
        buy_volume: Number((el?.buy_volume / 100)?.toFixed(1)),
        sell_volume: Number((el?.sell_volume / 100)?.toFixed(1)),
      };

      if (isEmptyBuy) {
        sellArr.push(modifiedEl);
      } else if (isEmptySell) {
        buyArr.push(modifiedEl);
      } else {
        buyArr.push({
          ...modifiedEl,
          sell_value: 0,
          sell_volume: 0,
          sell_avg: 0,
        });
        sellArr.push({
          ...modifiedEl,
          buy_value: 0,
          buy_volume: 0,
          buy_avg: 0,
        });
      }
    });

    // sort buyArr and sellArr based on highest volume and convert to JSON.stringify
    buyArr = orderBy([...buyArr], ["buy_volume"], ["desc"])?.map((el) =>
      JSON.stringify(el),
    );
    sellArr = orderBy([...sellArr], ["sell_volume"], ["desc"])?.map((el) =>
      JSON.stringify(el),
    );

    // generate final result to show
    const baseMappingArr =
      buyArr?.length >= sellArr?.length ? [...buyArr] : [...sellArr];
    const result = baseMappingArr?.map((el, idx) => {
      const parsedObj = JSON.parse(el);

      const isEmptyBuy =
        !parsedObj?.buy_volume && !parsedObj?.buy_value && !parsedObj?.buy_avg;

      if (isEmptyBuy) {
        // based mapping arr is sellArr

        // get and delete first element of buy arr
        const foundBuyOnlyFirstEl = buyArr?.shift();

        if (foundBuyOnlyFirstEl) {
          const parsedBuyOnlyFirstElement = JSON.parse(foundBuyOnlyFirstEl);

          return {
            sell_code: (
              <Tooltip
                title={<span style={{ fontSize: "0.8rem" }}>{parsedObj?.broker_company_name}</span>}
                arrow
                placement="top-start"
              >
                <p className="cursor-pointer inline-block">
                  {parsedObj?.code}
                </p>
              </Tooltip>
            ),
            sell_volume: (
              <p className='text-[#E52A49]'>
                {formatNumber.toMioBioTio(parsedObj?.sell_volume || 0, 1)}
              </p>
            ),
            sell_value: (
              <p className='text-[#E52A49]'>
                {formatNumber.toMioBioTio(parsedObj?.sell_value || 0, 1)}
              </p>
            ),
            sell_avg: (
              <p className='text-[#E52A49]'>
                {formatNumber.toMioBioTio(parsedObj?.sell_avg || 0, 1)}
              </p>
            ),
            idx: idx + 1,
            buy_code: (
              <Tooltip
                title={<span style={{ fontSize: "0.8rem" }}>{parsedBuyOnlyFirstElement?.broker_company_name}</span>}
                arrow
                placement="top-start"
              >
                <p className="cursor-pointer inline-block">
                  {parsedBuyOnlyFirstElement?.code}
                </p>
              </Tooltip>
            ),
            buy_volume: (
              <p className='text-[#039052]'>
                {formatNumber.toMioBioTio(
                  parsedBuyOnlyFirstElement?.buy_volume || 0,
                  1,
                )}
              </p>
            ),
            buy_value: (
              <p className='text-[#039052]'>
                {formatNumber.toMioBioTio(
                  parsedBuyOnlyFirstElement?.buy_value || 0,
                  1,
                )}
              </p>
            ),
            buy_avg: (
              <p className='text-[#039052]'>
                {formatNumber.toMioBioTio(
                  parsedBuyOnlyFirstElement?.buy_avg || 0,
                  1,
                )}
              </p>
            ),
          };
        }

        // not found buyOnlyFirstEl means this will generate empty buy row
        return {
          sell_code: (
            <Tooltip
              title={<span style={{ fontSize: "0.8rem" }}>{parsedObj?.broker_company_name}</span>}
              arrow
              placement="top-start"
            >
              <p className="cursor-pointer inline-block">
                {parsedObj?.code}
              </p>
            </Tooltip>
          ),
          sell_volume: (
            <p className='text-[#E52A49]'>
              {formatNumber.toMioBioTio(parsedObj?.sell_volume || 0, 1)}
            </p>
          ),
          sell_value: (
            <p className='text-[#E52A49]'>
              {formatNumber.toMioBioTio(parsedObj?.sell_value || 0, 1)}
            </p>
          ),
          sell_avg: (
            <p className='text-[#E52A49]'>
              {formatNumber.toMioBioTio(parsedObj?.sell_avg || 0, 1)}
            </p>
          ),
          idx: idx + 1,
          buy_code: null,
          buy_volume: null,
          buy_value: null,
          buy_avg: null,
        };
      } else {
        // based mapping arr is buyArr

        // get and delete first element of sell arr
        const foundSellOnlyFirstEl = sellArr?.shift();

        if (foundSellOnlyFirstEl) {
          const parsedSellOnlyFirstElement = JSON.parse(foundSellOnlyFirstEl);

          return {
            sell_code: (
              <Tooltip
                title={<span style={{ fontSize: "0.8rem" }}>{parsedSellOnlyFirstElement?.broker_company_name}</span>}
                arrow
                placement="top-start"
              >
                <p className="cursor-pointer inline-block">
                  {parsedSellOnlyFirstElement?.code}
                </p>
              </Tooltip>
            ),
            sell_volume: (
              <p className='text-[#E52A49]'>
                {formatNumber.toMioBioTio(
                  parsedSellOnlyFirstElement?.sell_volume || 0,
                  1,
                )}
              </p>
            ),
            sell_value: (
              <p className='text-[#E52A49]'>
                {formatNumber.toMioBioTio(
                  parsedSellOnlyFirstElement?.sell_value || 0,
                  1,
                )}
              </p>
            ),
            sell_avg: (
              <p className='text-[#E52A49]'>
                {formatNumber.toMioBioTio(
                  parsedSellOnlyFirstElement?.sell_avg || 0,
                  1,
                )}
              </p>
            ),
            idx: idx + 1,
            buy_code: (
              <Tooltip
                title={<span style={{ fontSize: "0.8rem" }}>{parsedObj?.broker_company_name}</span>}
                arrow
                placement="top-start"
              >
                <p className="cursor-pointer inline-block">
                  {parsedObj?.code}
                </p>
              </Tooltip>
            ),
            buy_volume: (
              <p className='text-[#039052]'>
                {formatNumber.toMioBioTio(parsedObj?.buy_volume || 0, 1)}
              </p>
            ),
            buy_value: (
              <p className='text-[#039052]'>
                {formatNumber.toMioBioTio(parsedObj?.buy_value || 0, 1)}
              </p>
            ),
            buy_avg: (
              <p className='text-[#039052]'>
                {formatNumber.toMioBioTio(parsedObj?.buy_avg || 0, 1)}
              </p>
            ),
          };
        }

        // not found sellOnlyFirstEl means this will generate empty sell row
        return {
          buy_code: (
            <Tooltip
              title={<span style={{ fontSize: "0.8rem" }}>{parsedObj?.broker_company_name}</span>}
              arrow
              placement="top-start"
            >
              <p className="cursor-pointer inline-block">
                {parsedObj?.code}
              </p>
            </Tooltip>
          ),
          buy_volume: (
            <p className='text-[#039052]'>
              {formatNumber.toMioBioTio(parsedObj?.buy_volume || 0, 1)}
            </p>
          ),
          buy_value: (
            <p className='text-[#039052]'>
              {formatNumber.toMioBioTio(parsedObj?.buy_value || 0, 1)}
            </p>
          ),
          buy_avg: (
            <p className='text-[#039052]'>
              {formatNumber.toMioBioTio(parsedObj?.buy_avg || 0, 1)}
            </p>
          ),
          idx: idx + 1,
          sell_code: null,
          sell_volume: null,
          sell_value: null,
          sell_avg: null,
        };
      }
    });

    // set modified total table row
    setTotal(result?.length);

    // manual pagination from FE
    return result.slice(
      page * brokerPerPage,
      page * brokerPerPage + brokerPerPage,
    );
  }, [brokerSummaryListResp?.data, page]);

  return (
    <div className='section'>
      <CustomTable
        id='broker-list-table'
        headCells={headCells}
        bodyCells={bodyCells ?? []}
        bodyCellKey='idx'
        total={total}
        rowsPerPage={brokerPerPage}
      />
    </div>
  );
}
